import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import {
  MAX_SENTRY_PAYLOAD_SIZE,
  TWO_TOOLBARS_ERROR_MESSAGE,
} from 'spw-constants';
import { UPLOAD_FLOORPLAN_SUCCEEDED } from 'spw-redux/modules/floorplan/actions';
import { IAppState } from 'spw-redux/reducer';
import { reduceObjectSizeToLimit } from 'spw-shared/utils';

const ignoredActions = [UPLOAD_FLOORPLAN_SUCCEEDED];

function findAncestorWithAriaLabel(el: Element) {
  let newEl = el.parentElement;
  while (newEl && !newEl.getAttribute('aria-label')) {
    newEl = newEl.parentElement;
  }
  return newEl;
}

type OriginalException = {
  message?: string;
  stack?: string;
};

const MAX_BREADCRUMBS = 50;

export const initSentry = () => {
  Sentry.init({
    dsn: process.env.SENTRY_DSN_WEB,
    release: process.env.SENTRY_RELEASE,
    maxBreadcrumbs: 100,
    integrations: [new Integrations.BrowserTracing()],
    environment: process.env.ENV,
    tracesSampleRate: 0,
    ignoreErrors: [
      /^Request failed with status code 401$/,
      /Response not successful: Received status code 401/,
      /Large Render Blocking Asset/,
      /ChunkLoadError/,
      /Loading CSS chunk/,
      'Non-Error promise rejection captured',
    ],
    // denyUrls: [/localhost:\d+/i, /127\.0\.0\.1:\d+/i],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    beforeBreadcrumb(breadcrumb, hint) {
      if (
        breadcrumb.category === 'redux.action' &&
        ignoredActions.includes(breadcrumb?.data?.type)
      ) {
        return null;
      }

      if (breadcrumb.category === 'ui.click') {
        const { target } = hint.event;

        if (
          target.getAttribute('aria-label') ||
          (target.innerText && !target.querySelector('[aria-label]')) ||
          findAncestorWithAriaLabel(target)?.getAttribute('aria-label')
        ) {
          // eslint-disable-next-line no-param-reassign
          breadcrumb.message = `${breadcrumb.message} > ${
            target.getAttribute('aria-label') ||
            target.innerText ||
            findAncestorWithAriaLabel(target)?.getAttribute('aria-label')
          }`;
        }
      }
      return breadcrumb;
    },
    normalizeDepth: 5,
    beforeSend(event, hint) {
      if (process.env.ENV === 'dev') {
        // eslint-disable-next-line no-console
        console.log(event, hint);
        return null;
      }

      if (
        /^ApolloError: Failed to fetch/.test(
          (hint?.originalException as OriginalException)?.stack
        )
      ) {
        return null;
      }

      let res: any = event;
      // first try to trim fat from the redux state
      res.contexts['redux.state'] = reduceObjectSizeToLimit(
        (event.contexts?.state?.state as { value: Record<string, unknown> })
          ?.value,
        MAX_SENTRY_PAYLOAD_SIZE
      );
      const state: IAppState | undefined = (event.contexts?.state?.state as {
        value: IAppState;
      })?.value;
      // eslint-disable-next-line no-param-reassign
      event.tags = event.tags || {};
      // eslint-disable-next-line no-param-reassign
      event.tags = {
        ...event.tags,
        projectType:
          state?.projectsReducer?.currentProject?.type ||
          'unknown project type',
        orgId: state?.userReducer?.user?.organizationId || 'unknown org',
      };
      // then trim the full event
      res = reduceObjectSizeToLimit(
        res as Record<string, unknown>,
        MAX_SENTRY_PAYLOAD_SIZE
      );

      if (
        process.env.ENV !== 'staging' ||
        res?.exception?.values?.[0]?.value !== TWO_TOOLBARS_ERROR_MESSAGE
      ) {
        res.breadcrumbs = res.breadcrumbs?.slice(-MAX_BREADCRUMBS);
      }

      return res;
    },
  });
};
